import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { useTranslation } from "react-i18next";
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import TopBarNav from '../styled/Topbar';
import { BeatLoader } from 'react-spinners';
import { fetchLanguages } from "../context/LanguageUtility";
import { GoogleLogin } from '@react-oauth/google';
import { useChat } from "../context/ChatProvider";
import { useNavigate } from 'react-router-dom';

const baseUrl = process.env.REACT_APP_SERVER;

/* ─── animations ─── */
const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 0.8; }
`;

/* ─── layout ─── */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  padding: 5rem 1rem 2rem;
  position: relative;
  overflow: hidden;
`;

const Orb = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 0; overflow: hidden;
  &::before, &::after {
    content: ''; position: absolute; border-radius: 50%; filter: blur(80px);
    animation: ${pulse} 6s ease-in-out infinite;
  }
  &::before { width: 400px; height: 400px; background: rgba(99,102,241,0.2); top: -8%; left: -8%; }
  &::after { width: 350px; height: 350px; background: rgba(236,72,153,0.15); bottom: -5%; right: -6%; animation-delay: 2s; }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  backdrop-filter: blur(16px);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const IntroText = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  line-height: 1.5;
`;

const Separator = styled.div`
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.85rem;
  width: 100%;
  max-width: 280px;
  text-align: center;
  margin: 0.2rem 0;
`;

const SocialWrapper = styled.div`
  width: 100%;
  max-width: 280px;
  display: flex;
  justify-content: center;
`;

/* ─── form ─── */
const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  max-width: 280px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.2s, background 0.2s;

  &::placeholder { color: rgba(255, 255, 255, 0.3); }
  &:focus { outline: none; border-color: rgba(99,102,241,0.5); background: rgba(255,255,255,0.08); }
`;

const PasswordWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordToggle = styled.span`
  position: absolute;
  top: 50%; right: 10px;
  transform: translateY(-50%);
  cursor: pointer;
  color: rgba(255,255,255,0.3);
  font-size: 1.3rem;
  display: flex;
  transition: color 0.2s;
  &:hover { color: rgba(255,255,255,0.6); }
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.7rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  -webkit-appearance: none;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(99,102,241,0.5); }
  & option { background: #1a1a2e; color: white; }
  @media (max-width: 768px) { padding: 1rem; }
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.8rem;
  color: rgba(255,255,255,0.5);
  cursor: pointer;
  width: 100%;
  line-height: 1.4;

  & a { color: #a5b4fc; text-decoration: none; &:hover { color: #c7d2fe; } }
`;

const StyledCheckbox = styled.input`
  margin: 0;
  margin-top: 2px;
  accent-color: #6366f1;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 0.7rem 0;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 14px rgba(99,102,241,0.3);
  margin-top: 0.3rem;

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 20px rgba(99,102,241,0.45); }
  &:active { transform: translateY(0); }
`;

const ErrorText = styled.p`
  font-size: 0.78rem;
  color: #fca5a5;
  margin: 0;
  line-height: 1.5;
  text-align: center;
`;

const MessageBox = styled.div`
  text-align: center;
  color: rgba(255,255,255,0.7);
  font-size: 0.9rem;
  line-height: 1.6;
  padding: 1rem 0;

  & span { color: rgba(255,255,255,0.85); }
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(30, 27, 75, 0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 10;
  border-radius: 24px;
`;

/* ─── component ─── */
const Signup = () => {
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [message, setMessage] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [supportedLanguages, setSupportedLanguages] = useState([]);
  const [languageConfiguration, setLanguageConfiguration] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [customLanguage, setCustomLanguage] = useState("");
  const [language, setLanguage] = useState("default");
  const { setUser, setToken } = useChat();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => { fetchLanguages(setSupportedLanguages, setLanguageConfiguration); }, []);

  const checkCustomLanguage = async (lang) => {
    const response = await axios.post(`${baseUrl}/lang/c`, { customLanguage: lang }, { withCredentials: true });
    if (!response.data.success) {
      setErrorMessage(response.data.suggestion ? `Did you mean '${response.data.suggestion}'?` : "Sorry we don't recognize that as a language.");
    }
    return response.data.success;
  };

  function getUsernameFromEmail(email) {
    const parts = email.split('@');
    return parts.length > 1 ? parts[0] : email;
  }

  const onSubmit = async (data) => {
    const { password, email } = data;
    setIsLoading(true);
    setErrorMessage("");

    if (language === 'Other') {
      try {
        if (!(await checkCustomLanguage(customLanguage))) { setIsLoading(false); return; }
      } catch (error) {
        setErrorMessage("Sorry we don't recognize that as a language.");
        setIsLoading(false);
        return;
      }
    }

    const selectedLanguage = language === 'Other' ? (customLanguage.charAt(0).toUpperCase() + customLanguage.slice(1)) : language;
    const displayName = getUsernameFromEmail(email);

    try {
      const response = await axios.post(`${baseUrl}/signup`, { username: displayName, password, email, language: selectedLanguage }, { withCredentials: true });
      setMessage(response.data.message);
      setIsSuccess(response.data.success);
    } catch (error) {
      setMessage("Signup failed. Please try again");
      setIsSuccess(false);
    }
    setIsLoading(false);
  };

  const googleSuccess = async (response) => {
    try {
      const backendResponse = await axios.post(`${baseUrl}/v/gtoken`, { idToken: response.credential }, { withCredentials: true });
      const { token, user } = backendResponse.data;
      if (token && user) {
        setToken(token);
        setUser(user);
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('token', JSON.stringify(token));
        sessionStorage.setItem('userLoggedInWithGoogle', "TRUE");
        navigate('/chat');
      }
    } catch (error) { /* silent */ }
  };

  const googleError = () => {};

  return (
    <PageWrapper>
      <Orb />
      <TopBarNav onSignInClick={() => {}} />

      <Card>
        {isLoading && <SpinnerOverlay><BeatLoader color="#a5b4fc" size={12} /></SpinnerOverlay>}

        {!isSuccess ? (
          <>
            <IntroText>{t('SignupIntroText')}</IntroText>

            <SocialWrapper>
              <GoogleLogin
                clientId={process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID}
                buttonText={t('GoogleSignin')}
                width="280"
                onSuccess={googleSuccess}
                onError={googleError}
                cookiePolicy={'single_host_origin'}
              />
            </SocialWrapper>

            <Separator>- or -</Separator>

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Input
                inputMode="email" maxLength={70}
                {...register("email", { required: true, pattern: /^\S+@\S+$/i })}
                type="email"
                placeholder={t('RegisterPageEmailText')}
              />
              {errors.email && <ErrorText>{t('SignupEmailValidate')}</ErrorText>}

              <PasswordWrapper>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder={t('SignupPasswordText')}
                  style={{ paddingRight: '2.5rem' }}
                  {...register("password", { required: true, pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{7,}$/ })}
                  maxLength={50}
                />
                <PasswordToggle onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
                </PasswordToggle>
              </PasswordWrapper>
              {errors.password && <ErrorText>Password must be at least 7 characters with uppercase, lowercase, and a number.</ErrorText>}

              <Select
                {...register("language", { required: true, validate: value => value !== "default" })}
                value={language}
                onChange={e => setLanguage(e.target.value)}
              >
                <option value="default">{t('SignupLanguageSelectDefault')}</option>
                {supportedLanguages.map((lang, index) => (
                  <option key={index} value={lang}>{t(lang)}</option>
                ))}
                <option value="Other">Other</option>
              </Select>
              {language === 'Other' && (
                <Input
                  type="text" placeholder="Specify your language"
                  value={customLanguage}
                  onChange={e => setCustomLanguage(e.target.value)}
                  required
                />
              )}
              {errors.language && <ErrorText>{t('SignupLanguageValidate')}</ErrorText>}
              {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

              <CheckboxRow>
                <StyledCheckbox type="checkbox" {...register("termsAndConditions", { required: true })} />
                <span dangerouslySetInnerHTML={{ __html: t('IAgreeToTheTermsAndConditions') }} />
              </CheckboxRow>
              {errors.termsAndConditions && <ErrorText>{t('TermsAndConditionsValidationMessage')}</ErrorText>}

              <SubmitButton type="submit">{t('CreateWithEmail')}</SubmitButton>
            </Form>
          </>
        ) : null}

        {message && <MessageBox><span dangerouslySetInnerHTML={{ __html: message }} /></MessageBox>}
      </Card>
    </PageWrapper>
  );
};

export default Signup;
